/**
 * QueryBuilder Store - The Zustand store itself.
 *
 * Composes the per-domain slices (see `slices/`) into one flat store.
 * Two coordinated layers:
 *   - builders[]:        one per Builder tab (rows, cols, stats, op,
 *                        universe, filterGroups, transpose).
 *   - transformPipelines: project-level transformation pipelines.
 *
 * Slices share this single state object, so the deliberate cross-slice
 * reads/writes (syncToProject resets, pipeline-removal rebinding, …) are
 * marked inline where they happen.
 *
 * Persistence: builders + pipelines + active id are persisted to
 * localStorage, scoped by projectId. Run results stay in-memory only.
 * The migrate ladder upgrades pre-v9 states through several waypoints —
 * see the inline notes in `migrations.ts` for the per-version handling.
 */
import { create } from "zustand"
import { persist } from "zustand/middleware"
import { QueryBuilderStore } from "./types"
import {
  QUERY_BUILDER_PERSIST_NAME,
  QUERY_BUILDER_PERSIST_VERSION,
  migrateQueryBuilderState,
  partializeQueryBuilderState,
} from "./migrations"
import { createBuilderSlice } from "./slices/builderSlice"
import { createLoopSlice } from "./slices/loopSlice"
import { createPipelineSlice } from "./slices/pipelineSlice"
import { createSessionSlice } from "./slices/sessionSlice"
import { createStepSlice } from "./slices/stepSlice"
import { createWeightingSlice } from "./slices/weightingSlice"

export const useQueryBuilderStore = create<QueryBuilderStore>()(
  persist(
    (...a) => ({
      ...createSessionSlice(...a),
      ...createBuilderSlice(...a),
      ...createPipelineSlice(...a),
      ...createStepSlice(...a),
      ...createLoopSlice(...a),
      ...createWeightingSlice(...a),
    }),
    {
      name: QUERY_BUILDER_PERSIST_NAME,
      version: QUERY_BUILDER_PERSIST_VERSION,
      migrate: migrateQueryBuilderState,
      partialize: partializeQueryBuilderState,
    }
  )
)
